import { logger } from '../../utils/logger';
import { getEnv } from '../../config/env';
import type { SnapshotHistoryEntry } from '../../types';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';

/**
 * Fetches migration run history for a recurring org sync migration.
 * Reuses GET /api/migrations/{id}/snapshot-history — the OrgSyncProcessor
 * appends each run to the same snapshotHistory array as snapshots.
 */
export async function fetchMigrationHistory(
  accessToken: string,
  migrationId: string
): Promise<SnapshotHistoryEntry[]> {
  const env = getEnv();
  const baseUrl = env.VITE_EDCI_API_URL.replace(/\/$/, '');
  const url = `${baseUrl}/api/migrations/${migrationId}/snapshot-history`;

  logger.info('Fetching migration history', { migrationId });

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(accessToken)
    });

    if (!response.ok) {
      logger.error('Failed to fetch migration history', {
        migrationId,
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to fetch migration history: ${response.statusText}`);
    }

    const history: SnapshotHistoryEntry[] = await response.json();
    logger.info('Migration history fetched successfully', { migrationId, count: history.length });
    return history;
  } catch (error) {
    logger.error('Error fetching migration history', { migrationId, error });
    throw error;
  }
}

/**
 * Downloads a specific historical migration run as a ZIP file.
 * Reuses GET /api/migrations/{id}/snapshots/{timestamp}/download — same
 * endpoint as snapshot downloads since the artifacts are stored identically.
 */
export async function downloadHistoricalMigration(
  accessToken: string,
  migrationId: string,
  timestamp: string
): Promise<void> {
  const env = getEnv();
  const baseUrl = env.VITE_EDCI_API_URL.replace(/\/$/, '');
  const url = `${baseUrl}/api/migrations/${migrationId}/snapshots/${timestamp}/download`;

  logger.info('Downloading historical migration', { migrationId, timestamp });

  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: getV2AuthHeaders(accessToken)
    });

    if (!response.ok) {
      logger.error('Failed to download historical migration', {
        migrationId,
        timestamp,
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Failed to download historical migration: ${response.statusText}`);
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `migration-${migrationId}-${timestamp}.zip`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch?.[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    logger.info('Historical migration downloaded successfully', {
      migrationId,
      timestamp,
      filename
    });
  } catch (error) {
    logger.error('Error downloading historical migration', { migrationId, timestamp, error });
    throw error;
  }
}
